import {
  SlidersHorizontal,
  Compass,
  MousePointerClick,
  Grid3x3,
  ToggleLeft,
  Scan,
  Palette,
  Type,
  Upload,
  Layers,
  AlignJustify,
  Eye,
  AlignLeft,
  Bookmark,
  List,
  ChevronDown,
  ToggleRight,
  Hash,
  Code2,
  Shuffle,
  Lock,
  ArrowUpDown,
  Sliders,
  Variable,
  Ruler,
  Rows3,
  Camera,
} from 'lucide-react';
import type { FeatureGroup } from '../types';

export const parametersGroup: FeatureGroup = {
  id: 'parameters',
  label: 'Parameter Controls',
  headline: 'The right control for every parameter',
  summary:
    'Meshpit automatically extracts parameters from your OpenSCAD code and maps them to purpose-built input widgets — not just sliders for everything. A single comment annotation is all it takes to get the perfect control for the job.',
  accentClass: 'from-sky-500 to-cyan-400',
  items: [
    {
      icon: SlidersHorizontal,
      title: 'Sliders & Extended Range',
      description:
        'Standard sliders for numeric values, with support for extended range and throttled realtime updates so the model rebuilds as you drag.',
      tier: 'major',
    },
    {
      icon: Compass,
      title: 'Angle Dial',
      description:
        'A circular dial that lets you rotate to set an angle. Triggered by a ° in the range comment — intuitive for rotation parameters that feel more natural as a dial than a number field.',
      tier: 'major',
    },
    {
      icon: MousePointerClick,
      title: 'XY & XYZ Pads',
      description:
        'Drag in 2D or 3D to control two or three values at once. Perfect for positioning, offsets, and vector parameters.',
      tier: 'major',
    },
    {
      icon: Grid3x3,
      title: 'Grid Input',
      description:
        'Pick values from an interactive 2D grid — ideal for parameters that represent a cell layout or a map of on/off states. Supports irregular flex-grid layouts with non-uniform cell sizes.',
      tier: 'major',
    },
    {
      icon: ToggleLeft,
      title: 'Stepper Input',
      description:
        'Click ← → buttons to step through a defined sequence of named values. Great for discrete states like size presets or material choices.',
      tier: 'major',
    },
    {
      icon: Scan,
      title: 'Polygon Input',
      description:
        'A sophisticated input for defining and editing polygon outlines — control vertex positions directly on a 2D canvas.',
      tier: 'major',
    },
    {
      icon: List,
      title: 'Stack Input',
      description:
        'A drag-reorderable list parameter. Items snap to a defined set of options; users add, remove, and reorder entries to build ordered sequences.',
      tier: 'major',
    },
    {
      icon: Palette,
      title: 'Color Picker',
      description:
        'Full-featured color picker with hex input, favorite swatches, and predefined palette options definable in code. 4-element arrays add an alpha/opacity slider.',
      tier: 'major',
    },
    {
      icon: Type,
      title: 'Font Input',
      description:
        'Browse and select from the fonts available in OpenSCAD. Live-preview the chosen typeface without leaving the parameters panel.',
      tier: 'major',
    },
    {
      icon: Upload,
      title: 'File Upload',
      description:
        'Attach external files — SVGs, DXFs, data files — as a parameter value. The model can reference the uploaded file at compile time.',
      tier: 'major',
    },
    {
      icon: Layers,
      title: 'SVG Group Picker',
      description:
        'When an SVG is attached to a model, pick individual groups or paths from it as a parameter value — in checkbox (multi-select) or radio (single-select) mode.',
      tier: 'major',
    },
    {
      icon: AlignJustify,
      title: 'Tabbed & Accordion Groups',
      description:
        'Organise parameters into named tabs or collapsible accordion sections in the sidebar. Groups can start collapsed by default — keeping complex models manageable.',
      tier: 'major',
    },
    {
      icon: Eye,
      title: 'Visibility Expressions',
      description:
        'Show or hide individual parameters or entire groups conditionally based on other parameter values — keep the panel clean and context-aware.',
      tier: 'major',
    },
    {
      icon: AlignLeft,
      title: 'Markdown in Parameters',
      description:
        'Embed formatted descriptions, section headings, links, and styled callout blocks (tip, note, warning, idea, error) directly in the parameters panel.',
      tier: 'major',
    },
    {
      icon: Bookmark,
      title: 'Parameter Snapshots',
      description:
        'Define named parameter presets directly in your SCAD source. Switch between configurations instantly, link to a specific snapshot from a Markdown block, or copy the current values as a snapshot with one click.',
      tier: 'major',
    },
    {
      icon: Code2,
      title: 'Code Expression Parameters',
      description:
        'Annotate a string with // [code] to turn it into a live OpenSCAD expression input — injected verbatim before compilation. Add a preset combobox with [code] [expr1:Label, expr2:Label] for quick-pick formulas.',
      tier: 'major',
    },
    {
      icon: ChevronDown,
      title: 'Dropdowns',
      description:
        'Number and string variables with a list of options in the comment render as dropdowns. Supports plain values, labelled options (value:Label), and the native OpenSCAD bracket syntax ([16:Draft, 32:Fine]).',
      tier: 'minor',
    },
    {
      icon: ToggleRight,
      title: 'Boolean Toggle',
      description:
        'A true/false variable automatically renders as a toggle switch — no annotation needed.',
      tier: 'minor',
    },
    {
      icon: Ruler,
      title: 'Range Input',
      description:
        'OpenSCAD range literals ([start:step:end]) render as three linked Start / Step / End number fields.',
      tier: 'minor',
    },
    {
      icon: Sliders,
      title: 'Vector Sliders',
      description:
        'A numeric array with axis labels in the comment (e.g. // [x, y, z]) renders each component as its own labelled slider — distinct from the drag-pad controls.',
      tier: 'minor',
    },
    {
      icon: Rows3,
      title: 'Color Swatches',
      description:
        'Define a list of hex colors in the parameter comment and they render as clickable swatches — one-click color switching without opening a picker.',
      tier: 'minor',
    },
    {
      icon: Variable,
      title: 'Dynamic Parameter Limits',
      description:
        'Range bounds can reference other parameters by name using the $ prefix — making min/max limits live and reactive as you adjust the model.',
      tier: 'minor',
    },
    {
      icon: Shuffle,
      title: 'Parameter Randomizer',
      description:
        'Randomize all unlocked parameters with one click or shortcut (⇧R). Supports proportional value change to scale parameters relative to each other.',
      tier: 'minor',
    },
    {
      icon: Lock,
      title: 'Lock & Pub/Sub Annotations',
      description:
        'Append @lock to exclude a parameter from randomization. Append @pubsub to wire it into the real-time inter-script pub/sub channel — texture and shader scripts can read and write it instantly.',
      tier: 'minor',
    },
    {
      icon: ArrowUpDown,
      title: 'Number Spinners & Unit Labels',
      description:
        'All number fields have up/down spinner buttons for precise incremental edits. Attach a custom unit suffix — mm, %, °, or anything else — with the unit= annotation.',
      tier: 'minor',
    },
    {
      icon: Hash,
      title: '$fn as a Parameter',
      description:
        'The OpenSCAD resolution variable $fn is surfaced automatically as a first-class parameter at the top of the panel — no annotation needed.',
      tier: 'minor',
    },
    {
      icon: Camera,
      title: 'Camera View in Parameters',
      description:
        'A camera_pip() call without overlay coordinates embeds a live secondary 3D viewport directly inside the parameters panel — useful for a fixed reference angle alongside your controls.',
      tier: 'minor',
    },
  ],
};
